import { Layout } from "@/components/layout";
import { Link } from "wouter";
import { useGetStatsOverview, getGetStatsOverviewQueryKey, useGetCategoryStats, getGetCategoryStatsQueryKey } from "@workspace/api-client-react";
import { useTrack } from "@/contexts/track";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, Crosshair, Target, Trophy, Clock, Zap, TrendingUp, TrendingDown, BarChart3, Radio, Crown, Lock, Info, ClipboardList, ChevronDown } from "lucide-react";
import { LeaderboardSheetBody } from "@/components/LeaderboardSheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useSubscription } from "@/contexts/subscription";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/language";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState, useEffect } from "react";
import { FlightLogbookPanel } from "@/pages/analytics";
import { SessionHistoryPanel } from "@/pages/session-history";
import { MilestonesPanel } from "@/components/MilestonesPanel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";

type CategoryStat = {
  category: string;
  totalQuestions: number;
  totalAnswered: number;
  totalCorrect: number;
  accuracy: number;
};

type Status = "CLEARED" | "ON COURSE" | "MARGINAL" | "TURBULENCE" | "CRITICAL" | "UNCHARTED";

function getStatus(cat: CategoryStat): Status {
  if (cat.totalAnswered === 0) return "UNCHARTED";
  if (cat.accuracy >= 80) return "CLEARED";
  if (cat.accuracy >= 70) return "ON COURSE";
  if (cat.accuracy >= 60) return "MARGINAL";
  if (cat.accuracy >= 40) return "TURBULENCE";
  return "CRITICAL";
}

const STATUS_CONFIG: Record<Status, { label: string; color: string; bg: string; border: string; dot: string }> = {
  CLEARED:     { label: "CLEARED",   color: "text-emerald-400",     bg: "bg-emerald-400/10",  border: "border-emerald-400/30", dot: "bg-emerald-400" },
  "ON COURSE": { label: "ON COURSE", color: "text-primary",          bg: "bg-primary/10",      border: "border-primary/30",    dot: "bg-primary" },
  MARGINAL:    { label: "MARGINAL",  color: "text-amber-400",       bg: "bg-amber-400/10",    border: "border-amber-400/30",  dot: "bg-amber-400" },
  TURBULENCE:  { label: "TURBULENCE",color: "text-orange-400",      bg: "bg-orange-400/10",   border: "border-orange-400/30", dot: "bg-orange-400" },
  CRITICAL:    { label: "CRITICAL",  color: "text-destructive",     bg: "bg-destructive/10",  border: "border-destructive/30",dot: "bg-destructive" },
  UNCHARTED:   { label: "UNCHARTED", color: "text-muted-foreground",bg: "bg-secondary/50",    border: "border-border",        dot: "bg-muted-foreground/40" },
};

function signalStrength(accuracy: number, answered: number): number {
  if (answered === 0) return 0;
  if (accuracy >= 80) return 5;
  if (accuracy >= 65) return 4;
  if (accuracy >= 50) return 3;
  if (accuracy >= 35) return 2;
  return 1;
}

function SignalBars({ accuracy, answered }: { accuracy: number; answered: number }) {
  const strength = signalStrength(accuracy, answered);
  const status: Status = answered === 0 ? "UNCHARTED" : accuracy >= 80 ? "CLEARED" : accuracy >= 70 ? "ON COURSE" : accuracy >= 60 ? "MARGINAL" : accuracy >= 40 ? "TURBULENCE" : "CRITICAL";
  const cfg = STATUS_CONFIG[status];
  return (
    <div className="flex items-end gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          className={cn("w-1.5 rounded-sm transition-colors", i <= strength ? cfg.dot : "bg-secondary/60")}
          style={{ height: `${6 + i * 3}px` }}
        />
      ))}
    </div>
  );
}

function AccuracyRing({ accuracy, answered }: { accuracy: number; answered: number }) {
  const status: Status = answered === 0 ? "UNCHARTED" : accuracy >= 80 ? "CLEARED" : accuracy >= 70 ? "ON COURSE" : accuracy >= 60 ? "MARGINAL" : accuracy >= 40 ? "TURBULENCE" : "CRITICAL";
  const cfg = STATUS_CONFIG[status];
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = answered === 0 ? 0 : (accuracy / 100) * circ;
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 72 72" className="w-20 h-20 -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" strokeWidth="6" className="stroke-secondary" />
        <circle
          cx="36" cy="36" r={r} fill="none" strokeWidth="6"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          className={cn(
            "transition-all duration-700",
            status === "CLEARED"    ? "stroke-emerald-400" :
            status === "ON COURSE"  ? "stroke-primary" :
            status === "MARGINAL"   ? "stroke-amber-400" :
            status === "TURBULENCE" ? "stroke-orange-400" :
            status === "CRITICAL"   ? "stroke-destructive" :
            "stroke-muted-foreground/30"
          )}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("text-lg font-black font-mono leading-none", cfg.color)}>
          {answered === 0 ? "—" : `${Math.round(accuracy)}%`}
        </span>
        <span className="text-[9px] font-mono text-muted-foreground/60 leading-none mt-0.5">ACC</span>
      </div>
    </div>
  );
}

function MetricNote({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full p-0.5 text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label="Metric explanation"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 text-xs leading-relaxed font-mono cursor-pointer"
        onClick={() => setOpen(false)}
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const { plan, getPlanForTrack } = useSubscription();
  const { selectedTrack, trackInfo } = useTrack();
  const trackPlan = getPlanForTrack(selectedTrack);
  const isProPlus = trackPlan === "pro_plus";
  const { t } = useLanguage();
  const d = t.dashboard;

  const [sessionHistoryOpen, setSessionHistoryOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [milestonesOpen, setMilestonesOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true,
  );
  const [mobileSessionSectionOpen, setMobileSessionSectionOpen] = useState(false);
  const isMobile = useIsMobile();
  const sheetSide = isMobile ? ("bottom" as const) : ("right" as const);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("scrollToFlightLogbook") !== "1") return;
      sessionStorage.removeItem("scrollToFlightLogbook");
      const id = window.setTimeout(() => {
        document.getElementById("flight-logbook")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => window.clearTimeout(id);
    } catch {
      /* private mode / quota */
    }
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("openSessionHistory") !== "1") return;
      sessionStorage.removeItem("openSessionHistory");
      setSessionHistoryOpen(true);
    } catch {
      /* private mode / quota */
    }
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("openLeaderboard") !== "1") return;
      sessionStorage.removeItem("openLeaderboard");
      setLeaderboardOpen(true);
    } catch {
      /* private mode / quota */
    }
  }, []);
  const STATUS_LABELS: Record<Status, string> = {
    CLEARED:      d.statusCleared,
    "ON COURSE":  d.statusOnCourse,
    MARGINAL:     d.statusMarginal,
    TURBULENCE:   d.statusTurbulence,
    CRITICAL:     d.statusCritical,
    UNCHARTED:    d.statusUncharted,
  };

  const { data: stats, isLoading: statsLoading } = useGetStatsOverview(
    { track: selectedTrack },
    { query: { queryKey: getGetStatsOverviewQueryKey({ track: selectedTrack }), staleTime: 120_000 } }
  );
  const { data: categories, isLoading: categoriesLoading } = useGetCategoryStats(
    { track: selectedTrack },
    { query: { queryKey: getGetCategoryStatsQueryKey({ track: selectedTrack }), staleTime: 120_000 } }
  );

  const categoryRows = Array.isArray(categories) ? categories : [];
  const sorted = [...categoryRows].sort((a, b) => {
    if (a.totalAnswered === 0 && b.totalAnswered > 0) return 1;
    if (b.totalAnswered === 0 && a.totalAnswered > 0) return -1;
    return a.accuracy - b.accuracy;
  });

  const answered = sorted.filter(c => c.totalAnswered > 0);
  const avgAccuracy = answered.length > 0
    ? answered.reduce((s, c) => s + c.accuracy, 0) / answered.length
    : 0;
  const strongest = answered.length > 0 ? [...answered].sort((a, b) => b.accuracy - a.accuracy)[0] : null;
  const weakest   = answered.length > 0 ? [...answered].sort((a, b) => a.accuracy - b.accuracy)[0] : null;
  const totalCoverage = categoryRows.length > 0
    ? Math.round((categoryRows.reduce((s, c) => s + c.totalAnswered, 0) / Math.max(1, categoryRows.reduce((s, c) => s + c.totalQuestions, 0))) * 100)
    : 0;

  return (
    <Layout>
      <div className="space-y-8 animate-in fade-in duration-500">

        {/* Plan upgrade banners */}
        {plan === "free" && selectedTrack === "cpl" && (
          <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-amber-500/40 bg-amber-500/10">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-300">{d.freePlanTitle}</p>
                <p className="text-xs text-amber-400/70">{d.freePlanSub}</p>
              </div>
            </div>
            <Link href="/subscription" className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium bg-amber-500 text-black hover:bg-amber-400 h-9 px-4 py-2 transition-colors">
              {d.viewPlans}
            </Link>
          </div>
        )}
        {plan === "pro" && selectedTrack === "cpl" && (
          <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-amber-500/40 bg-amber-500/10">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-300">{d.proPlanTitle}</p>
                <p className="text-xs text-amber-400/70">{d.proPlanSub}</p>
              </div>
            </div>
            <Link href="/subscription" className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium bg-amber-500 text-black hover:bg-amber-400 h-9 px-4 py-2 transition-colors">
              {d.passYourExam}
            </Link>
          </div>
        )}

        {/* Title row */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{d.title}</h1>
            <p className="text-muted-foreground">{d.subtitle}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Sheet open={leaderboardOpen} onOpenChange={setLeaderboardOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 border-input bg-background hover:bg-accent hover:text-accent-foreground"
                >
                  <Trophy className="mr-2 h-4 w-4" />
                  {d.openLeaderboard}
                </Button>
              </SheetTrigger>
              <SheetContent
                side={sheetSide}
                className={cn(
                  "flex w-full flex-col gap-0 overflow-hidden p-0",
                  isMobile
                    ? "max-h-[min(92dvh,100%)] rounded-t-2xl border-t"
                    : "h-full max-h-[100dvh] border-l sm:max-w-lg",
                )}
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>{d.openLeaderboard}</SheetTitle>
                </SheetHeader>
                <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 sm:px-5", isMobile ? "pt-10" : "pt-12")}>
                  {leaderboardOpen ? <LeaderboardSheetBody /> : null}
                </div>
              </SheetContent>
            </Sheet>
            <Sheet open={sessionHistoryOpen} onOpenChange={setSessionHistoryOpen}>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="hidden h-10 border-input bg-background hover:bg-accent hover:text-accent-foreground md:inline-flex"
                >
                  <ClipboardList className="mr-2 h-4 w-4" />
                  {d.openSessionHistory}
                </Button>
              </SheetTrigger>
              <SheetContent
                side={sheetSide}
                className={cn(
                  "flex w-full flex-col gap-0 overflow-hidden p-0",
                  isMobile
                    ? "max-h-[min(92dvh,100%)] rounded-t-2xl border-t"
                    : "h-full max-h-[100dvh] border-l sm:max-w-lg",
                )}
              >
                <SheetHeader className="sr-only">
                  <SheetTitle>{d.openSessionHistory}</SheetTitle>
                </SheetHeader>
                <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 sm:px-5", isMobile ? "pt-10" : "pt-12")}>
                  {sessionHistoryOpen ? <SessionHistoryPanel embedded /> : null}
                </div>
              </SheetContent>
            </Sheet>
            <Link href="/weaknesses" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2">
              <Target className="w-4 h-4 mr-2" />
              {d.tackleturb}
            </Link>
            <Link href="/practice" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
              <Crosshair className="w-4 h-4 mr-2" />
              {d.requestTakeoff}
            </Link>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 border-border/50">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 min-h-[4rem]">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight flex items-center gap-1.5">
                  {d.waypointsCleared}
                  <MetricNote text={d.waypointsClearedNote} />
                </CardTitle>
              <Activity className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            </CardHeader>
            <CardContent>
              {statsLoading ? <Skeleton className="h-8 w-16" /> : (
                <div className="text-3xl font-bold font-mono">{stats?.totalAnswered || 0}</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 min-h-[4rem]">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight flex items-center gap-1.5">
                  {d.accuracy}
                  <MetricNote text={d.accuracyNote} />
                </CardTitle>
              <Target className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            </CardHeader>
            <CardContent>
              {statsLoading ? <Skeleton className="h-8 w-16" /> : (
                <div className="text-3xl font-bold font-mono">{stats?.overallAccuracy ? Math.round(stats.overallAccuracy) : 0}%</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 min-h-[4rem]">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight flex items-center gap-1.5">
                  {d.flightsLogged}
                  <MetricNote text={d.flightsLoggedNote} />
                </CardTitle>
              <Trophy className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            </CardHeader>
            <CardContent>
              {statsLoading ? <Skeleton className="h-8 w-16" /> : (
                <div className="text-3xl font-bold font-mono">{stats?.sessionsCompleted || 0}</div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 min-h-[4rem]">
                <CardTitle className="text-sm font-medium text-muted-foreground leading-tight flex items-center gap-1.5">
                  {d.currentStreak}
                  <MetricNote text={d.currentStreakNote} />
                </CardTitle>
              <Clock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            </CardHeader>
            <CardContent>
              {statsLoading ? <Skeleton className="h-8 w-16" /> : (
                <div className="text-3xl font-bold font-mono">{stats?.currentStreak || 0} <span className="text-base text-muted-foreground font-sans font-normal">{d.days}</span></div>
              )}
            </CardContent>
          </Card>
        </div>

        <Collapsible open={milestonesOpen} onOpenChange={setMilestonesOpen}>
          <div className="rounded-lg border border-border bg-card/40 overflow-hidden">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <div className="min-w-0">
                  <div className="text-sm font-bold font-mono uppercase tracking-widest text-foreground">{d.collapsibleProgressTitle}</div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{d.collapsibleProgressSub}</div>
                </div>
                <ChevronDown
                  className={cn(
                    "h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200",
                    milestonesOpen && "rotate-180",
                  )}
                  aria-hidden
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t border-border/60 px-2 pb-3 pt-1 sm:px-3">
                <MilestonesPanel />
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        <div className="md:hidden">
          <Collapsible open={mobileSessionSectionOpen} onOpenChange={setMobileSessionSectionOpen}>
            <div className="rounded-lg border border-border bg-card/40 overflow-hidden">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <ClipboardList className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <div className="min-w-0">
                      <div className="text-sm font-bold font-mono uppercase tracking-widest text-foreground">{d.openSessionHistory}</div>
                      <div className="text-[11px] text-muted-foreground font-mono mt-0.5 line-clamp-2">{d.sessionHistoryMobileHint}</div>
                    </div>
                  </div>
                  <ChevronDown
                    className={cn(
                      "h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200",
                      mobileSessionSectionOpen && "rotate-180",
                    )}
                    aria-hidden
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="border-t border-border/60 px-4 pb-4 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-input bg-background font-mono text-xs h-10"
                    onClick={() => {
                      setSessionHistoryOpen(true);
                      setMobileSessionSectionOpen(false);
                    }}
                  >
                    <ClipboardList className="mr-2 h-4 w-4" />
                    {d.openSessionHistoryPanelCta}
                  </Button>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        {/* Ground Status */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>{d.groundStatus}</CardTitle>
            <CardDescription>{d.groundStatusSub}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-8">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">{d.hangarCapacity}</span>
                {statsLoading ? <Skeleton className="h-5 w-10" /> : <span className="text-sm font-mono text-primary">{stats?.totalQuestions || 0}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">{d.dispatchStatus}</span>
                {statsLoading ? <Skeleton className="h-5 w-10" /> : <span className="text-sm font-mono text-primary">{stats?.totalQuestions ? d.clearedToFly : d.grounded}</span>}
              </div>
              {(!stats?.totalQuestions || stats.totalQuestions === 0) && !statsLoading && (
                <Link href="/import" className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3">
                  {d.goToImport}
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Sector Map ── */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-foreground">{d.sectorMap}</h2>
              <p className="text-sm text-muted-foreground">{d.sectorMapSub}</p>
            </div>
            {!isProPlus && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold font-mono px-3 py-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300">
                <Crown className="w-3.5 h-3.5" />PRO PLUS ADVANCED
              </span>
            )}
          </div>

          {!isProPlus ? (
            <div className="relative">
              <div className="space-y-6 select-none pointer-events-none blur-sm opacity-30" aria-hidden="true">
                <div className="grid grid-cols-3 gap-4">
                  {[{ l: "Avg Accuracy", v: "74%" }, { l: "Bank Coverage", v: "38%" }, { l: "Sectors Scanned", v: "4/8" }].map(({ l, v }) => (
                    <div key={l} className="border border-border rounded-lg bg-card p-4 text-center">
                      <div className="text-2xl font-black font-mono text-foreground">{v}</div>
                      <div className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-wider">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[{ cat: "Air Law", acc: 72, cov: 45 }, { cat: "Navigation", acc: 58, cov: 31 }, { cat: "Meteorology", acc: 81, cov: 62 }, { cat: "Aircraft Technical", acc: 49, cov: 18 }].map(({ cat, acc, cov }) => (
                    <div key={cat} className="border border-border rounded-lg bg-card p-5 flex gap-4 items-center">
                      <div className="w-20 h-20 rounded-full border-4 border-secondary flex items-center justify-center shrink-0">
                        <span className="text-xl font-black font-mono">{acc}%</span>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="font-semibold text-foreground">{cat}</div>
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-primary/60 rounded-full" style={{ width: `${acc}%` }} />
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-muted-foreground/40 rounded-full" style={{ width: `${cov}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-card border border-amber-400/30 rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl text-center">
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/15 border border-amber-400/30 mx-auto mb-4">
                    <Lock className="w-7 h-7 text-amber-300" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground mb-1">{d.deepRadarTitle}</h2>
                  <p className="text-xs font-mono text-amber-300 tracking-widest mb-4">{d.deepRadarBadge}</p>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    {d.deepRadarBody}
                    {trackPlan === "pro" && (
                      <span className="block mt-2 text-primary/80">
                        {d.deepRadarProNote}
                      </span>
                    )}
                  </p>
                  <Link
                    href="/subscription"
                    className="inline-flex items-center justify-center gap-2 w-full py-3 bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold rounded transition-colors"
                  >
                    <Crown className="w-4 h-4" />
                    {d.upgradeBtn}
                  </Link>
                  <p className="text-white/30 text-xs mt-3">{d.upgradePrice}</p>
                </div>
              </div>
            </div>
          ) : categoriesLoading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-36 w-full" />)}
              </div>
            </div>
          ) : categoryRows.length > 0 ? (
            <div className="space-y-6">

              {/* Summary strip */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border border-border rounded-lg bg-card p-4 text-center">
                  <div className={cn(
                    "text-3xl font-black font-mono",
                    avgAccuracy >= 80 ? "text-emerald-400" : avgAccuracy >= 60 ? "text-amber-400" : "text-destructive"
                  )}>
                    {answered.length > 0 ? `${Math.round(avgAccuracy)}%` : "—"}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-wider">{d.avgAccuracy}</div>
                </div>
                <div className="border border-border rounded-lg bg-card p-4 text-center">
                  <div className="text-3xl font-black font-mono text-foreground">{totalCoverage}%</div>
                  <div className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-wider">{d.bankCoverage}</div>
                </div>
                <div className="border border-border rounded-lg bg-card p-4 text-center">
                  <div className="text-3xl font-black font-mono text-foreground">
                    {answered.length}<span className="text-muted-foreground text-xl">/{categoryRows.length}</span>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-wider">{d.sectorsScanned}</div>
                </div>
              </div>

              {/* Strongest / Weakest */}
              {strongest && weakest && strongest.category !== weakest.category && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-emerald-400/20 bg-emerald-400/5 rounded-lg p-4 flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-emerald-400/70 uppercase tracking-wider">{d.strongestSector}</div>
                      <div className="font-semibold text-foreground truncate">{strongest.category}</div>
                      <div className="text-sm font-mono text-emerald-400">{Math.round(strongest.accuracy)}% {d.accuracyWord}</div>
                    </div>
                  </div>
                  <div className="border border-destructive/20 bg-destructive/5 rounded-lg p-4 flex items-center gap-3">
                    <TrendingDown className="w-5 h-5 text-destructive shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-destructive/70 uppercase tracking-wider">{d.weakestSector}</div>
                      <div className="font-semibold text-foreground truncate">{weakest.category}</div>
                      <div className="text-sm font-mono text-destructive">{Math.round(weakest.accuracy)}% {d.accuracyWord}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Category grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sorted.map((cat) => {
                  const status = getStatus(cat);
                  const cfg = STATUS_CONFIG[status];
                  const coverage = cat.totalQuestions > 0
                    ? Math.round((cat.totalAnswered / cat.totalQuestions) * 100)
                    : 0;
                  const remaining = cat.totalQuestions - cat.totalAnswered;
                  return (
                    <div
                      key={cat.category}
                      className={cn(
                        "border rounded-lg bg-card p-5 space-y-4 transition-colors hover:border-primary/30",
                        status === "CRITICAL" ? "border-destructive/30" :
                        status === "CLEARED"  ? "border-emerald-400/20" : "border-border"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <AccuracyRing accuracy={cat.accuracy} answered={cat.totalAnswered} />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-foreground leading-tight">{cat.category}</h3>
                            <span className={cn(
                              "shrink-0 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border",
                              cfg.color, cfg.bg, cfg.border
                            )}>
                              {STATUS_LABELS[status]}
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                              <span>{d.accuracyLabel}</span>
                              <span className={cfg.color}>{cat.totalAnswered > 0 ? `${Math.round(cat.accuracy)}%` : "—"}</span>
                            </div>
                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                              <div
                                className={cn(
                                  "h-full rounded-full transition-all duration-700",
                                  status === "CLEARED"    ? "bg-emerald-400" :
                                  status === "ON COURSE"  ? "bg-primary" :
                                  status === "MARGINAL"   ? "bg-amber-400" :
                                  status === "TURBULENCE" ? "bg-orange-400" :
                                  status === "CRITICAL"   ? "bg-destructive" :
                                  "bg-muted-foreground/20"
                                )}
                                style={{ width: `${cat.totalAnswered > 0 ? cat.accuracy : 0}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                              <span>{d.coverageLabel}</span>
                              <span>{coverage}%</span>
                            </div>
                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-muted-foreground/40 transition-all duration-700" style={{ width: `${coverage}%` }} />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-border pt-3 flex items-center justify-between">
                        <div className="grid grid-cols-3 gap-3 text-center flex-1">
                          <div>
                            <div className="text-sm font-mono font-bold text-foreground">{cat.totalAnswered}</div>
                            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{d.attemptedLabel}</div>
                          </div>
                          <div>
                            <div className="text-sm font-mono font-bold text-foreground">{cat.totalCorrect}</div>
                            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{d.correctLabel}</div>
                          </div>
                          <div>
                            <div className="text-sm font-mono font-bold text-foreground">{cat.totalQuestions}</div>
                            <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{d.inBankLabel}</div>
                          </div>
                        </div>
                        <div className="pl-4 border-l border-border ml-3">
                          <SignalBars accuracy={cat.accuracy} answered={cat.totalAnswered} />
                          <div className="text-[9px] font-mono text-muted-foreground/50 mt-1 text-center">{d.signalLabel}</div>
                        </div>
                      </div>
                      {remaining > 0 && cat.totalAnswered > 0 && (
                        <div className="text-[10px] font-mono text-muted-foreground/50 text-right -mt-2">
                          {(remaining === 1 ? d.qNotYetAttempted : d.qNotYetAttemptedPlural).replace("{n}", String(remaining))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-lg bg-card/50 p-12 flex flex-col items-center justify-center text-center space-y-4">
              <div className="p-4 bg-secondary rounded-full">
                <Radio className="w-8 h-8 text-muted-foreground animate-pulse" />
              </div>
              <h3 className="text-xl font-bold">{d.noDataTitle}</h3>
              <p className="text-muted-foreground max-w-sm">
                {d.noDataBody}
              </p>
              <Link
                href="/practice"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-mono font-bold rounded hover:bg-primary/90 transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                START FLYING
              </Link>
            </div>
          )}
        </div>

        <FlightLogbookPanel variant="section" />

      </div>
    </Layout>
  );
}
